/**
 * Programmer une période.
 *
 * ## Ce que cet écran remplace
 *
 * `AdminSessionsPage` ouvrait un modal de treize champs par session. Mesure de
 * la charge réelle, du 8 septembre 2026 :
 *
 * | Période | Sessions | Jours de saisie |
 * |---|---|---|
 * | Été 2026 | 76 | 5 |
 * | Détente 2026 | 49 | 1 |
 * | Printemps 2026 | 47 | 3 |
 * | Été 2025 | 95 | 41 |
 *
 * Soit ~990 champs pour l'été 2026, dont 304 saisies de prix — pour cinq jeux
 * de prix distincts et sept semaines de calendrier.
 *
 * ## Deux déclarations, et une seule est un préalable
 *
 * 1. **Les semaines** — les dates et les jours ouvrés, une fois par semaine de
 *    congé au lieu de trois champs sur chaque session. C'est un préalable
 *    parce qu'un stage se programme SUR une semaine.
 * 2. **Programmer** — un stage, les semaines et les centres cochés, un jeu de
 *    prix : toutes les sessions d'un coup.
 *
 * Rien d'autre à déclarer d'avance. Une version précédente exigeait un barème
 * par (centre, stage) avant de pouvoir programmer, ce qui faisait saisir quatre
 * fois les mêmes chiffres pour « les Poussins à 84 € dans quatre centres » —
 * la même répétition, remontée d'un étage.
 *
 * ## L'ancienne liste reste
 *
 * Le second onglet est `AdminSessionsPage` : les filtres, l'archivage, la liste
 * d'attente et la modification à l'unité n'avaient pas de raison d'être
 * réécrits. Ce qui disparaît, c'est la saisie session par session comme SEUL
 * moyen de programmer.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarDays,
  ListOrdered,
  Loader2,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import clsx from 'clsx';
import toast, { Toaster } from 'react-hot-toast';

import { useAdminStore } from '../../stores/adminStore';
import { useStageStore } from '../../stores/stageStore';
import { useTarifConditionStore } from '../../stores/tarifConditionStore';
import {
  anneesConnues,
  periodes as chargerPeriodes,
  semainesDeLaPeriode,
  sessionsProgrammees,
  type Periode,
  type Semaine,
  type SessionProgrammee,
} from '../../lib/programme/gateway.supabase';
import SemainesDeLaPeriode from './components/programme/SemainesDeLaPeriode';
import ProgrammerUnStage from './components/programme/ProgrammerUnStage';
import SessionsProgrammees from './components/programme/SessionsProgrammees';
import AdminSessionsPage from './AdminSessionsPage';
import { CHEMINS } from '../../lib/navigation/chemins';
import { estUneJpa } from '../../lib/jpa/regles';

type Onglet = 'programmer' | 'sessions';

const AdminProgrammationPage = () => {
  const { centers, fetchCenters } = useAdminStore();
  const { stages, fetchStages } = useStageStore();
  const { conditions, fetchConditions } = useTarifConditionStore();

  const [onglet, setOnglet] = useState<Onglet>('programmer');

  const [periodes, setPeriodes] = useState<Periode[]>([]);
  const [annees, setAnnees] = useState<number[]>([]);
  const [periodId, setPeriodId] = useState<string>('');
  const [annee, setAnnee] = useState<number>(new Date().getFullYear());

  const [semaines, setSemaines] = useState<Semaine[]>([]);
  const [programme, setProgramme] = useState<SessionProgrammee[]>([]);

  const [chargement, setChargement] = useState(true);
  const [rafraichissement, setRafraichissement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const signaler = useCallback((message: string) => {
    setErreur(message);
    toast.error(message, { duration: 8000 });
  }, []);

  /* --- Le référentiel, une fois --- */
  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        const [p, a] = await Promise.all([chargerPeriodes(), anneesConnues()]);
        if (!vivant) return;
        setPeriodes(p);
        setAnnees(a);
        setPeriodId((actuel) => actuel || p[0]?.id || '');
      } catch (err: any) {
        if (vivant) signaler(err?.message ?? 'Les périodes n’ont pas pu être chargées.');
      } finally {
        if (vivant) setChargement(false);
      }
    })();
    fetchCenters();
    fetchStages();
    fetchConditions();
    return () => {
      vivant = false;
    };
  }, [fetchCenters, fetchStages, fetchConditions, signaler]);

  /* --- Semaines et sessions, à chaque changement de (période, année) --- */
  const recharger = useCallback(
    async (silencieux = false) => {
      if (!periodId) return;
      if (!silencieux) setRafraichissement(true);
      try {
        const [s, p] = await Promise.all([
          semainesDeLaPeriode(periodId, annee),
          sessionsProgrammees(periodId, annee),
        ]);
        setSemaines(s);
        setProgramme(p);
        setErreur(null);
      } catch (err: any) {
        signaler(err?.message ?? 'La période n’a pas pu être chargée.');
      } finally {
        setRafraichissement(false);
      }
    },
    [periodId, annee, signaler],
  );

  useEffect(() => {
    recharger(true);
  }, [recharger]);

  const periodeLabel = useMemo(
    () => periodes.find((p) => p.id === periodId)?.label ?? '',
    [periodes, periodId],
  );

  /*
    Les centres et les stages proposés : les actifs seulement. Huit des 28
    stages n'ont jamais porté de session et encombraient la liste déroulante de
    l'ancien modal à chaque programmation. Et parmi les centres, les centres de
    vacances seuls : un stage ne se programme pas ailleurs (VALIDÉ ÉCLOSION,
    20/10/2026 ; la base le refuse aussi).
  */
  const centresProposes = useMemo<{ id: string; name: string }[]>(
    () =>
      centers
        .filter((c) => c.active !== false && c.archived !== true && c.centre_de_vacances !== false)
        .map((c) => ({ id: c.id, name: c.name })),
    [centers],
  );
  /*
    La fiche « Journée pédagogique animée » n'est pas un stage à programmer :
    une journée se programme dans Journées pédagogiques, avec son détail
    (`jpa_journees`) — programmée ici, elle n'en aurait pas.
  */
  const stagesProposes = useMemo<{ id: string; title: string }[]>(
    () =>
      stages.filter((s) => s.active !== false && !estUneJpa(s)).map((s) => ({ id: s.id, title: s.title })),
    [stages],
  );

  if (chargement) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Programmation des stages</h1>
          <p className="text-gray-600 mt-1">
            Déclarez les semaines d’une période, puis programmez un stage dans plusieurs
            centres d’un coup.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => recharger()}
            disabled={rafraichissement}
            className="btn-outline flex items-center"
          >
            <RefreshCw className={clsx('h-5 w-5 mr-2', rafraichissement && 'animate-spin')} />
            Actualiser
          </button>
          <Link to={CHEMINS.planification.listeAttente} className="btn-outline flex items-center">
            <ListOrdered className="h-5 w-5 mr-2" />
            Liste d’attente
          </Link>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {(
            [
              ['programmer', 'Programmer une période', Sparkles],
              ['sessions', 'Toutes les sessions', ListOrdered],
            ] as const
          ).map(([cle, libelle, Icone]) => (
            <button
              key={cle}
              onClick={() => setOnglet(cle)}
              className={clsx(
                'flex items-center gap-2 pb-3 -mb-px border-b-2 font-medium text-sm transition-colors',
                onglet === cle
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800',
              )}
            >
              <Icone className="h-4 w-4" />
              {libelle}
            </button>
          ))}
        </nav>
      </div>

      {erreur && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg flex items-start">
          <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm">{erreur}</p>
        </div>
      )}

      {onglet === 'sessions' ? (
        <AdminSessionsPage sansEntete />
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="form-label">Période</label>
                <select
                  value={periodId}
                  onChange={(e) => setPeriodId(e.target.value)}
                  className="form-input min-w-[180px]"
                >
                  {periodes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Année</label>
                <select
                  value={annee}
                  onChange={(e) => setAnnee(Number(e.target.value))}
                  className="form-input min-w-[120px]"
                >
                  {annees.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 ml-auto text-sm">
                <div
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-full border',
                    semaines.length > 0
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-primary-50 border-primary-200 text-primary-800',
                  )}
                >
                  <CalendarDays className="h-4 w-4" />
                  <span className="font-medium">Semaines</span>
                  <span className="tabular-nums">{semaines.length}</span>
                </div>
                <div
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-full border',
                    programme.length > 0
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-gray-50 border-gray-200 text-gray-400',
                  )}
                >
                  <Sparkles className="h-4 w-4" />
                  <span className="font-medium">Sessions</span>
                  <span className="tabular-nums">{programme.length}</span>
                </div>
              </div>
            </div>
          </div>

          <SemainesDeLaPeriode
            periodId={periodId}
            periodeLabel={periodeLabel}
            annee={annee}
            semaines={semaines}
            onChangement={() => recharger(true)}
            onErreur={signaler}
          />

          <ProgrammerUnStage
            periodId={periodId}
            periodeLabel={periodeLabel}
            annee={annee}
            semaines={semaines}
            centres={centresProposes}
            stages={stagesProposes}
            conditions={conditions}
            dejaProgramme={programme}
            onProgramme={() => recharger(true)}
            onErreur={signaler}
          />

          <SessionsProgrammees
            periodeLabel={periodeLabel}
            annee={annee}
            sessions={programme}
            conditions={conditions}
            onChangement={() => recharger(true)}
            onErreur={signaler}
          />
        </div>
      )}

      <Toaster position="top-right" />
    </div>
  );
};

export default AdminProgrammationPage;
